import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { Select, InputNumber, Alert, Modal, Button } from 'antd';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import { cloneDeep } from 'lodash';
import React from 'react';
import { getClient } from '../../apollo';
import {
  VehicleTypesQuery,
  GetVehicleBrandModelsQuery,
  useVehicleTypesQuery,
  useGetVehicleBrandModelsQuery,
  useCreateVehiclePriceVariableMutation,
  useUpdateVehiclePriceVariableMutation,
  GetARateQuery,
  GetARateQueryVariables,
  GetARateDocument,
} from '../../graphql/datamodel.gen';
import { nullIfUndefined } from '../../utils/form';

interface VariablesModalProps {
  visible: boolean;
  onClose: () => any;
  variable?: GetARateQuery['rate']['vehiclePriceVariables'][number];
  rateId: string;
  clientId?: string;
}

interface VariablesFormProps extends FormComponentProps {
  variable?: GetARateQuery['rate']['vehiclePriceVariables'][number];
  types: VehicleTypesQuery['vehicleTypes'];
  models: GetVehicleBrandModelsQuery['vehicleBrandModels'][number]['models'];
  brands: GetVehicleBrandModelsQuery['vehicleBrandModels'];
  onSubmit: (data: IVariablesFormValues) => any;
}

interface VariablesFormState {
  validationError?: string;
}

interface IVariablesFormValues {
  id?: string;
  vehicleTypeId?: string;
  vehicleModelId?: string;
  vehicleBrandId?: string;
  numPassengers?: number;
  extraKm?: number;
  extraToll?: number;
  extraHourPrice?: number;
  airport?: number;
  parkingMontserrat?: number;
}

class VariablesForm extends React.Component<
  VariablesFormProps,
  VariablesFormState
> {
  state = {
    validationError: undefined,
  };

  handleSubmit = (e) => {
    e.preventDefault();

    this.props.form.validateFields((err, values: IVariablesFormValues) => {
      if (!err) {
        if (
          !values.vehicleTypeId &&
          !values.vehicleBrandId &&
          !values.vehicleModelId
        ) {
          return this.setState({
            validationError:
              'Debes seleccionar almenos un desplegable: un tipo, marca o modelo (o los tres)',
          });
        }

        this.props.onSubmit({
          extraHourPrice: values.extraHourPrice,
          vehicleModelId: nullIfUndefined(values.vehicleModelId),
          vehicleTypeId: nullIfUndefined(values.vehicleTypeId),
          parkingMontserrat: values.parkingMontserrat,
          numPassengers: values.numPassengers,
          extraToll: values.extraToll,
          extraKm: values.extraKm,
          airport: values.airport,
          id: this.props.variable?.id,
          vehicleBrandId: nullIfUndefined(values.vehicleBrandId),
        });
      }
    });
  };

  render() {
    const { getFieldDecorator } = this.props.form;

    return (
      <Form
        id={'variables-form'}
        layout={'vertical'}
        onSubmit={this.handleSubmit}
      >
        <Form.Item label={'Tipo'}>
          {getFieldDecorator('vehicleTypeId', {
            initialValue: this.props.variable?.vehicleTypeId,
          })(
            <Select allowClear={true} placeholder={'Selecciona un tipo'}>
              {this.props.types.map((type) => {
                return (
                  <Select.Option key={type.id} value={type.id}>
                    {type.name.toUpperCase()}
                  </Select.Option>
                );
              })}
            </Select>,
          )}
        </Form.Item>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Form.Item label={'Marca'} style={{ flex: 1, marginRight: '15px' }}>
            {getFieldDecorator('vehicleBrandId', {
              initialValue: this.props.variable?.vehicleBrandId,
            })(
              <Select allowClear={true} placeholder={'Selecciona una marca'}>
                {this.props.brands.map((brand) => {
                  return (
                    <Select.Option key={brand.id} value={brand.id}>
                      {brand.brand.toUpperCase()}
                    </Select.Option>
                  );
                })}
              </Select>,
            )}
          </Form.Item>
          <Form.Item label={'Modelo'} style={{ flex: 1 }}>
            {getFieldDecorator('vehicleModelId', {
              initialValue: this.props.variable?.vehicleModelId,
            })(
              <Select allowClear={true} placeholder={'Selecciona un modelo'}>
                {this.props.models.map((model) => {
                  return (
                    <Select.Option key={model.id} value={model.id}>
                      {model.name.toUpperCase()}
                    </Select.Option>
                  );
                })}
              </Select>,
            )}
          </Form.Item>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Form.Item
            label={'Capacidad (PAX)'}
            style={{ flex: 1, marginRight: '15px' }}
          >
            {getFieldDecorator('numPassengers', {
              initialValue: this.props.variable?.paxNumber || 0,
            })(<InputNumber min={0} step={1} />)}
          </Form.Item>
          <Form.Item label={'Kilómetro extra'} style={{ flex: 1 }}>
            {getFieldDecorator('extraKm', {
              initialValue: this.props.variable?.extraPricePerKm || 0,
            })(<InputNumber min={0} step={0.1} />)}
            {' €'}
          </Form.Item>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Form.Item label={'Peaje'} style={{ flex: 1, marginRight: '15px' }}>
            {getFieldDecorator('extraToll', {
              initialValue: this.props.variable?.tollPricePerKm || 0,
            })(<InputNumber min={0} step={0.1} />)}
            {' €'}
          </Form.Item>
          <Form.Item label={'Asistencia Aeropuerto'} style={{ flex: 1 }}>
            {getFieldDecorator('airport', {
              initialValue: this.props.variable?.airportPrice || 0,
            })(<InputNumber min={0} step={0.1} />)}
            {' €'}
          </Form.Item>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Form.Item
            label={'Parking Montserrat'}
            style={{ flex: 1, marginRight: '15px' }}
          >
            {getFieldDecorator('parkingMontserrat', {
              initialValue: this.props.variable?.parkingMontserrat || 0,
            })(<InputNumber min={0} step={0.1} />)}
            {' €'}
          </Form.Item>
          <Form.Item label={'Hora extra'} style={{ flex: 1 }}>
            {getFieldDecorator('extraHourPrice', {
              initialValue: this.props.variable?.extraHourPrice || 0,
            })(<InputNumber min={0} step={0.1} />)}
            {' €'}
          </Form.Item>
        </div>
        {this.state.validationError && (
          <Form.Item>
            <Alert
              type={'error'}
              description={this.state.validationError}
              message={'Error de validación'}
            />
          </Form.Item>
        )}
      </Form>
    );
  }
}

const MyVariablesForm = Form.create<VariablesFormProps>()(VariablesForm);

export const VariablesModal: React.FC<VariablesModalProps> = (
  props: VariablesModalProps,
) => {
  const vehicleTypesQuery = useVehicleTypesQuery({
    client: getClient(),
    fetchPolicy: 'network-only',
  });

  const vehicleBrandModelsQuery = useGetVehicleBrandModelsQuery({
    client: getClient(),
    fetchPolicy: 'network-only',
  });

  const [createVehiclePriceVariableMut] = useCreateVehiclePriceVariableMutation(
    {
      client: getClient(),
    },
  );

  const [updateVehiclePriceVariableMut] = useUpdateVehiclePriceVariableMutation(
    {
      client: getClient(),
    },
  );

  const brands = vehicleBrandModelsQuery?.data?.vehicleBrandModels || [];
  const allModels = [];
  const types = vehicleTypesQuery?.data?.vehicleTypes;

  brands.map((brand) => {
    allModels.push(...(brand.models || []));
  });

  const onSubmit = async (data: IVariablesFormValues) => {
    try {
      if (!data.id) {
        // Create
        await createVehiclePriceVariableMut({
          variables: {
            rateID: props.rateId,
            airportPrice: data.airport,
            extraKmPrice: data.extraKm,
            extraHourPrice: data.extraHourPrice,
            extraTollPrice: data.extraToll,
            numPassengers: data.numPassengers,
            parkingMontserratPrice: data.parkingMontserrat,
            vehicleTypeId: data.vehicleTypeId,
            vehicleBrandId: data.vehicleBrandId,
            vehicleModelId: data.vehicleModelId,
          },
          update: (proxy, mutationResult) => {
            try {
              const previousData = getClient().readQuery<
                GetARateQuery,
                GetARateQueryVariables
              >({
                query: GetARateDocument,
                id: props.rateId,
                variables: {
                  id: props.rateId,
                },
              });
              const updatedRate = cloneDeep({
                ...previousData.rate,
                vehiclePriceVariables: [
                  ...previousData.rate.vehiclePriceVariables,
                  mutationResult.data?.createVehiclePriceVariable,
                ],
              });
              getClient().writeQuery<GetARateQuery, GetARateQueryVariables>({
                query: GetARateDocument,
                data: {
                  rate: updatedRate,
                },
                id: props.rateId,
                variables: {
                  id: props.rateId,
                },
              });
              // @ts-ignore
              getClient().queryManager.broadcastQueries();
            } catch (e) {
              console.error(e);
            }
          },
        });
      } else {
        // Create
        await updateVehiclePriceVariableMut({
          variables: {
            id: data.id,
            airportPrice: data.airport,
            extraKmPrice: data.extraKm,
            extraTollPrice: data.extraToll,
            extraHourPrice: data.extraHourPrice,
            numPassengers: data.numPassengers,
            parkingMontserratPrice: data.parkingMontserrat,
            vehicleTypeId: data.vehicleTypeId,
            vehicleBrandId: data.vehicleBrandId,
            vehicleModelId: data.vehicleModelId,
          },
        });
      }
      props.onClose();
    } catch (e) {
      console.error(
        'Error when creating or updating vehicle price variable',
        e,
      );
    }
  };

  return (
    <Modal
      title={
        props.variable ? 'Gestionar configuración' : 'Añadir configuración'
      }
      footer={[
        <Button danger onClick={props.onClose} style={{ float: 'left' }}>
          {props.variable ? 'Cerrar sin guardar' : 'Cerrar'}
        </Button>,
        <Button type={'primary'} htmlType={'submit'} form={'variables-form'}>
          {props.variable ? 'Guardar cambios' : 'Añadir'}
        </Button>,
      ]}
      onCancel={props.onClose}
      visible={props.visible}
      centered={true}
      closable={true}
      // maskClosable={true}
      // forceRender={true}
      destroyOnClose={true}
    >
      <MyVariablesForm
        {...props}
        onSubmit={onSubmit}
        types={types}
        models={allModels}
        brands={brands}
      />
    </Modal>
  );
};
